import Vue from 'vue'
import configs from '@/config'
import store from '@/store'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import clientSubscription from '@/utils/client.subscription'
import constants from '@/constant'
import logger from '@/logger'

const logFn = logger.extend('answersApi')
let resource = null
// Because version 1, we don't have result ID yet due to descoped features.
// SO I will use this const to fake the result ID
const TEMP_CATEGORISE_ID = '-1'
const actions = {
  getMyQuizAnswers: {
    method: 'GET',
    url: `${configs.baseUrl}/api/answers/quiz?presentationId={presentationId}&audienceId={audienceId}`,
  },
  createWordCloudAnswerViaLiveProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answers/wordcloud`,
  },
  createWordCloudAnswerViaLiveProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answers/wordcloud`,
  },
  createWordCloudAnswerViaLiveApi: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/wordcloud`,
  },
  createWordCloudAnswerV1: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide-option/audience-create-wordcloud`,
  },
  createPollAnswerViaLiveProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answers/poll`,
  },
  createPollAnswerViaLiveProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answers/poll`,
  },
  createPollAnswerViaLiveApi: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/poll`,
  },
  createPollAnswerV1: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create`,
  },
  createScaleAnswerViaLiveProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answers/scale`,
  },
  createScaleAnswerViaLiveProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answers/scale`,
  },
  createScaleAnswerViaLiveApi: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/scale`,
  },
  createScaleAnswerV1: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create`,
  },
  createQuizAnswer: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create`,
  },
  createQuizAnswerThroughProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answer/create`,
  },
  createQuizAnswerThroughProxyV2: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answer/create/v2`,
  },
  createQuizAnswerThroughProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answer/create/v2`,
  },
  createQuizAnswerGeneralApiV3: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create/v3`,
  },
  createCategoriseAnswer: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/categories`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

const getMyQuizAnswers = async ({ presentationId, audienceId }, params) => {
  const res = await resource.getMyQuizAnswers({ presentationId, audienceId }, params)

  return res.body
}

/**
 * Extract query params from payload to append as querystring
 * for tracking purpose. We want to know which answer submission
 * belongs to which slide
 * @param {*} payload
 * @returns
 */
const extractQueryParamsFromPayload = (payload) => {
  try {
    if (!store.getters['logger/canTrackSlideIdForPostReqs'] || !payload) {
      return undefined
    }

    const slideId = payload.slideId || (['string', 'number'].includes(typeof payload.slide) ? payload.slide : undefined)

    if (slideId) {
      return { slideId }
    }

    return undefined
  } catch (err) {
    logFn('extractQueryParamsFromPayload', err)
    return undefined
  }
}

const createWordCloudAnswer = async (payload) => {
  try {
    logFn('createWordCloudAnswer', payload)
    const queryParams = extractQueryParamsFromPayload(payload)
    if (store.getters['liveStatsV2/canUseWordCloudLiveProxy']) {
      const res = queryParams ? await resource.createWordCloudAnswerViaLiveProxy(queryParams, payload)
        : await resource.createWordCloudAnswerViaLiveProxy(payload)
      return res.body
    }
    if (store.getters['liveStatsV2/canUseWordCloudLiveApi']) {
      const res = queryParams ? await resource.createWordCloudAnswerViaLiveApi(queryParams, payload)
        : await resource.createWordCloudAnswerViaLiveApi(payload)
      return res.body
    }
    const res = queryParams ? await resource.createWordCloudAnswerV1(queryParams, payload)
      : await resource.createWordCloudAnswerV1(payload)
    return res.body
  } catch (error) {
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return { body: { error: error.message } }
  }
}

const createPollAnswer = async (payload) => {
  try {
    logFn('createPollAnswer', payload)
    const queryParams = extractQueryParamsFromPayload(payload)
    if (store.getters['liveStatsV2/canUsePollLiveProxy']) {
      const res = queryParams ? await resource.createPollAnswerViaLiveProxy(queryParams, payload)
        : await resource.createPollAnswerViaLiveProxy(payload)
      return res.body
    }
    if (store.getters['liveStatsV2/canUsePollLiveApi']) {
      const res = queryParams ? await resource.createPollAnswerViaLiveApi(queryParams, payload)
        : await resource.createPollAnswerViaLiveApi(payload)
      return res.body
    }
    const res = queryParams ? await resource.createPollAnswerV1(queryParams, payload)
      : await resource.createPollAnswerV1(payload)
    return res.body
  } catch (error) {
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return { body: { error: error.message } }
  }
}

const createScaleAnswer = async (payload) => {
  try {
    logFn('createScaleAnswer', payload)
    const queryParams = extractQueryParamsFromPayload(payload)
    if (store.getters['liveStatsV2/canUseScaleLiveProxy']) {
      const res = queryParams ? await resource.createScaleAnswerViaLiveProxy(queryParams, payload)
        : await resource.createScaleAnswerViaLiveProxy(payload)
      return res.body
    }
    if (store.getters['liveStatsV2/canUseScaleLiveApi']) {
      const res = queryParams ? await resource.createScaleAnswerViaLiveApi(queryParams, payload)
        : await resource.createScaleAnswerViaLiveApi(payload)
      return res.body
    }
    const res = queryParams ? await resource.createScaleAnswerV1(queryParams, payload)
      : await resource.createScaleAnswerV1(payload)
    return res.body
  } catch (error) {
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return { body: { error: error.message } }
  }
}

const createQuizAnswer = async (payload) => {
  try {
    logFn('createQuizAnswer', payload)
    const queryParams = extractQueryParamsFromPayload(payload)
    if (store.getters['liveStatsV2/canUseLiveStatsForQuizV2']) {
      if (queryParams) {
        await resource.createQuizAnswerThroughProxyV2(queryParams, payload)
      } else {
        await resource.createQuizAnswerThroughProxyV2(payload)
      }
      const key = `${clientSubscription.constantObjectSubcriptionType.submitQuizAnswerType}-${payload.slideId}`
      return clientSubscription.subscribe(key)
    }

    const res = queryParams ? await resource.createQuizAnswer(queryParams, payload)
      : await resource.createQuizAnswer(payload)
    return res.body
  } catch (error) {
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return { body: { error: error.message } }
  }
}

const createCategoriseAnswer = async (payload) => {
  logFn('createCategoriseAnswer', payload)
  const queryParams = extractQueryParamsFromPayload(payload)
  const res = queryParams ? await resource.createCategoriseAnswer(queryParams, payload)
    : await resource.createCategoriseAnswer(payload)
  res.body.id = TEMP_CATEGORISE_ID
  return res.body
}

export default {
  initResource,
  getMyQuizAnswers,
  createWordCloudAnswer,
  createScaleAnswer,
  createPollAnswer,
  createCategoriseAnswer,
  createQuizAnswer,
}
